import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  errorToast,
  successToast,
  handleErrors,
  safeHandleGenericError,
} from "@/app/_module/services/helper";
import { hybridUploadService } from "@/app/_module/services/hybridUploadService";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";

import { resourcesService } from "../services/services";
// Change Resource to ResourceType
import type { ResourceType, ResourceFilters, UpdateResourceRequest, CreateResourceRequest } from "../types/types";

// Memoized agency context to prevent infinite re-renders
let cachedAgencyContext: { agencyId: string | null } | null = null;
let lastAgencyId: string | null = null;

// Helper function to get current agency context for query keys
const getAgencyContext = () => {
  const agencyId = AgencyTokenManager.getCurrentAgencyId();
  
  // Only create new context if agency ID has changed
  if (agencyId !== lastAgencyId) {
    lastAgencyId = agencyId;
    cachedAgencyContext = agencyId ? { agencyId } : { agencyId: null };
  }
  
  return cachedAgencyContext!;
};

export const resourceKeys = {
  all: () => ["resources", getAgencyContext()] as const,
  lists: () => ["resources", "list", getAgencyContext()] as const,
  list: (filters: ResourceFilters) => ["resources", "list", filters, getAgencyContext()] as const,
  details: () => ["resources", "detail", getAgencyContext()] as const,
  detail: (id: string) => ["resources", "detail", id, getAgencyContext()] as const,
  categories: () => ["resources", "categories", getAgencyContext()] as const,
  carriers: () => ["resources", "carriers", getAgencyContext()] as const,
};

export const useResources = (filters?: ResourceFilters) => {
  return useQuery({
    queryKey: resourceKeys.list(filters || {}),
    queryFn: async () => {
      const data = await resourcesService.getResources(filters);
      return data;
    },
    staleTime: 30 * 1000, // 30 seconds for more real-time feel
    refetchOnMount: true, // Ensure fresh data on mount
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    refetchInterval: 60 * 1000, // Poll every 60 seconds for true real-time updates
  });
};

export const useResourceById = (id: string) => {
  return useQuery({
    queryKey: resourceKeys.detail(id),
    queryFn: async () => {
      const data = await resourcesService.getResourceById(id);
      return data;
    },
    enabled: !!id,
    staleTime: 30 * 1000,
    refetchOnMount: true, // Ensure fresh data on mount
    refetchOnWindowFocus: true,
  });
};

export const useResourceCategories = () => {
  return useQuery({
    queryKey: resourceKeys.categories(),
    queryFn: async () => {
      const data = await resourcesService.getCategories();
      return data;
    },
    staleTime: 60 * 1000, // 1 minute (categories rarely change)
    refetchOnMount: true, // Ensure fresh data on mount
    refetchOnWindowFocus: true,
  });
};

export const useAgencyCarriers = () => {
  const agencyId = AgencyTokenManager.getCurrentAgencyId();
  
  return useQuery({
    queryKey: resourceKeys.carriers(),
    queryFn: async () => {
      // Pass agencyId to filter carriers by agency
      const data = await resourcesService.getAgencyCarriers(agencyId || undefined);
      return data;
    },
    staleTime: 60 * 1000, // 1 minute (carriers rarely change)
    refetchOnMount: true, // Ensure fresh data on mount
    refetchOnWindowFocus: true,
  });
};

export const useCreateResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateResourceRequest) => {
      // Use hybrid upload with hidden progress (no UI indicators)
      const result = await resourcesService.createResource({
        ...data,
        // Hidden progress callbacks - hybrid works but no UI shows
        onProgress: () => {}, // Silent progress
        onStatusUpdate: () => {} // Silent status updates
      });
      
      // Handle hybrid upload result silently
      if ('isProcessing' in result && result.isProcessing) {
        // Start silent polling for background processing completion
        hybridUploadService.startPolling(
          result.uploadId,
          () => {}, // Silent status updates
          async (uploadStatus) => {
            try {
              // Complete the resource creation silently
              await resourcesService.completeResourceCreation(
                result.uploadId,
                {
                  title: data.title,
                  description: data.description,
                  categoryId: data.categoryId,
                  carrier: data.carrier,
                  fileType: data.fileType,
                  tags: data.tags,
                  visibility: data.visibility,
                  featured: data.featured
                },
                {
                  downloadUrl: uploadStatus.downloadUrl!,
                  filePath: uploadStatus.filePath,
                  fileSize: uploadStatus.fileSize,
                  mimeType: uploadStatus.mimeType
                }
              );

              // Invalidate cache to trigger refetch and show new resource
              // Use partial key match to invalidate ALL resource list queries regardless of filters
              await queryClient.invalidateQueries({ queryKey: ["resources", "list"] });

              // Silent completion - user sees the result in the list
            } catch (error) {

              // Silent error handling - no user notification
            }
          }
        );

        // Return success immediately for UI - hybrid processing happens in background
        return result.partialData as ResourceType;
      } else {
        // Immediate success
        return result as ResourceType;
      }
    },
    onSuccess: async () => {
      successToast("Resource created successfully!");

      // Invalidate cache to trigger refetch and show new resource immediately
      // Use partial key match to invalidate ALL resource list queries regardless of filters
      await queryClient.invalidateQueries({ queryKey: ["resources", "list"] });
    },
    onError: (error: unknown) => {

      // Handle specific category validation errors
      const errorMessage = safeHandleGenericError(error, (err) => {
        if (err.message?.toLowerCase().includes('category')) {
          return 'Invalid category selection. Please choose a valid category and try again.';
        }
        if (err.message?.toLowerCase().includes('validation')) {
          return 'Validation failed. Please check your inputs and try again.';
        }
        return err.message || 'Failed to create resource';
      });
      
      errorToast(errorMessage);
    },
  });
};

export const useUpdateResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateResourceRequest }) =>
      resourcesService.updateResource(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches - use partial match to cancel all list queries
      await queryClient.cancelQueries({ queryKey: ["resources", "list"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({ queryKey: ["resources", "list"] });

      // Optimistically update resource in cache
      queryClient.setQueriesData({ queryKey: ["resources", "list"] }, (old: any) => {
        if (!old) return old;
        return old.map((resource: any) =>
          resource.id === id
            ? { ...resource, ...data, updatedAt: new Date().toISOString() }
            : resource
        );
      });

      return { previousData };
    },
    onError: (error: unknown, vars, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      // Handle specific category validation errors
      const errorMessage = safeHandleGenericError(error, (err) => {
        if (err.message?.toLowerCase().includes('category')) {
          return 'Invalid category selection. Please choose a valid category and try again.';
        }
        if (err.message?.toLowerCase().includes('validation')) {
          return 'Validation failed. Please check your inputs and try again.';
        }
        return err.message || 'Failed to update resource';
      });

      errorToast(errorMessage);
    },
    onSuccess: async () => {
      successToast("Resource updated successfully!");
      // Use partial key match to invalidate ALL resource list queries regardless of filters
      await queryClient.invalidateQueries({ queryKey: ["resources", "list"] });
    },
  });
};

export const useDeleteResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => resourcesService.deleteResource(id),
    onMutate: async (resourceId) => {
      // Cancel any outgoing refetches - use partial match to cancel all list queries
      await queryClient.cancelQueries({ queryKey: ["resources", "list"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({ queryKey: ["resources", "list"] });

      // Optimistically remove resource from cache
      queryClient.setQueriesData({ queryKey: ["resources", "list"] }, (old: any) => {
        if (!old) return old;
        return old.filter((resource: any) => resource.id !== resourceId);
      });

      return { previousData };
    },
    onError: (error: unknown, vars, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      errorToast(safeHandleGenericError(error, handleErrors));
    },
    onSuccess: async () => {
      successToast("Resource deleted successfully!");
      // Use partial key match to invalidate ALL resource list queries regardless of filters
      await queryClient.invalidateQueries({ queryKey: ["resources", "list"] });
    },
  });
};

export const useLikeResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => resourcesService.likeResource(id),
    onMutate: async (resourceId) => {
      // Cancel any outgoing refetches - use partial match to cancel all list queries
      await queryClient.cancelQueries({ queryKey: ["resources", "list"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({ queryKey: ["resources", "list"] });

      // Optimistically update the cache - use ResourceType instead of Resource
      queryClient.setQueriesData(
        { queryKey: ["resources", "list"] },
        (old: any) => {
          if (!old) return old;
          if (!Array.isArray(old)) return old;

          return old.map((resource: ResourceType) => {
            if (resource.id === resourceId) {
              return {
                ...resource,
                isLiked: true,
                likeCount: (resource.likeCount || 0) + 1,
              };
            }
            return resource;
          });
        }
      );

      return { previousData };
    },
    onSuccess: () => {
      successToast("Resource liked successfully!");
    },
    onError: (error: unknown, resourceId, context) => {
      // Revert cache changes on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      errorToast(safeHandleGenericError(error, handleErrors));
    },
    onSettled: () => {
      // Always refetch to get the latest data from backend
      // Use partial key match to invalidate ALL resource list queries regardless of filters
      void queryClient.invalidateQueries({ queryKey: ["resources", "list"] });
    },
  });
};

export const useUnlikeResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => resourcesService.unlikeResource(id),
    onMutate: async (resourceId) => {
      // Cancel any outgoing refetches - use partial match to cancel all list queries
      await queryClient.cancelQueries({ queryKey: ["resources", "list"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({ queryKey: ["resources", "list"] });

      // Optimistically update the cache - use ResourceType instead of Resource
      queryClient.setQueriesData(
        { queryKey: ["resources", "list"] },
        (old: any) => {
          if (!old) return old;
          if (!Array.isArray(old)) return old;

          return old.map((resource: ResourceType) => {
            if (resource.id === resourceId) {
              return {
                ...resource,
                isLiked: false,
                likeCount: Math.max((resource.likeCount || 0) - 1, 0),
              };
            }
            return resource;
          });
        }
      );

      return { previousData };
    },
    onSuccess: () => {
      successToast("Resource unliked successfully!");
    },
    onError: (error: unknown, resourceId, context) => {
      // Revert cache changes on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      errorToast(safeHandleGenericError(error, handleErrors));
    },
    onSettled: () => {
      // Always refetch to get the latest data from backend
      // Use partial key match to invalidate ALL resource list queries regardless of filters
      void queryClient.invalidateQueries({ queryKey: ["resources", "list"] });
    },
  });
};

// NEW: Hook for tracking downloads with optimistic updates
export const useTrackDownload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => resourcesService.trackDownload?.(id) || Promise.resolve({ downloadCount: 0 }),
    onMutate: async (resourceId) => {
      // Cancel any outgoing refetches - use partial match to cancel all list queries
      await queryClient.cancelQueries({ queryKey: ["resources", "list"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({ queryKey: ["resources", "list"] });

      // Optimistically update the download count - use ResourceType
      queryClient.setQueriesData(
        { queryKey: ["resources", "list"] },
        (old: any) => {
          if (!old) return old;
          if (!Array.isArray(old)) return old;

          return old.map((resource: ResourceType) => {
            if (resource.id === resourceId) {
              return {
                ...resource,
                downloadCount: (resource.downloadCount || 0) + 1,
              };
            }
            return resource;
          });
        }
      );

      return { previousData };
    },
    onSuccess: () => {
      // Silently update - no toast needed for download tracking
    },
    onError: (error: unknown, resourceId, context) => {
      // Revert cache changes on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      // Don't show error toast for download tracking as it's background operation

    },
    onSettled: () => {
      // Invalidate resource queries to update download counts
      // Use partial key match to invalidate ALL resource list queries regardless of filters
      queryClient.invalidateQueries({ queryKey: ["resources", "list"] });
    },
  });
};

// NEW: Hook for cleanup on component unmount
export const useResourceCleanup = () => {
  const cleanup = () => {
    hybridUploadService.cleanup();
  };

  return { cleanup };
};

// Hook to get sub-agency IDs for permission checks
// Returns array of agency IDs that are descendants of the current agency
export const useSubAgencyIds = () => {
  return useQuery({
    queryKey: ["subAgencies", "ids", getAgencyContext()],
    queryFn: async () => {
      try {
        const result = await resourcesService.getSubAgencyIds();
        return result;
      } catch {
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - hierarchy doesn't change often
    retry: 1,
  });
};